import React from 'react';
import { useEmployee } from '../contexts/EmployeeContext';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission: string;
}

/**
 * Wraps a route to check if the current employee has permission to access it.
 * If not, shows an access denied message or redirects to dashboard.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, permission }) => {
  const { isLoggedIn, hasPermission, employee } = useEmployee();

  // If no employee is logged in, let the lock screen handle it
  if (!isLoggedIn) {
    return <>{children}</>;
  }

  // Check if employee has the required permission
  if (!hasPermission(permission)) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldExclamationIcon className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-600 mb-4">
            Sorry {employee?.first_name}, you don't have permission to access this page.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Contact your administrator if you need access to this feature.
          </p>
          <a
            href="/"
            className="inline-block px-6 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-blue-hover transition-colors"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Employee has permission - render the page
  return <>{children}</>;
};
